import React, { useEffect, useState } from 'react';
import { FileText, Loader2, Plus, Upload, Download, Trash2, History } from 'lucide-react';
import { toast } from 'sonner';
import { documentosApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { hasRole } from '../lib/utils';

const formatDateTime = (iso) => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return d.toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

export default function DocumentosPage() {
  const { user } = useAuth();
  const canWrite = hasRole(user, ['analista', 'admin', 'comercial']);
  const canDelete = hasRole(user, 'admin');

  const [loading, setLoading] = useState(true);
  const [docs, setDocs] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [nuevoTitulo, setNuevoTitulo] = useState('');
  const [nuevoFile, setNuevoFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const [historialDoc, setHistorialDoc] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await documentosApi.list();
      setDocs(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cargar documentos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCrear = async (e) => {
    e.preventDefault();
    if (!nuevoTitulo.trim() || !nuevoFile) {
      toast.error('Ingresá un título y seleccioná un archivo');
      return;
    }
    setSaving(true);
    try {
      await documentosApi.create(nuevoTitulo.trim(), nuevoFile);
      toast.success('Documento creado');
      setDialogOpen(false);
      setNuevoTitulo('');
      setNuevoFile(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear documento');
    } finally {
      setSaving(false);
    }
  };

  const handleNuevaVersion = async (doc, file) => {
    if (!file) return;
    try {
      await documentosApi.uploadVersion(doc.documento_id, file);
      toast.success(`Nueva versión subida (v${doc.version + 1})`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir versión');
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`¿Eliminar definitivamente "${doc.titulo}" y todas sus versiones?`)) return;
    try {
      await documentosApi.delete(doc.documento_id);
      toast.success('Documento eliminado');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  return (
    <div className="p-4 md:p-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
            <FileText className="w-5 h-5 text-indigo-700" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Documentos</h1>
            <p className="text-slate-500 mt-1">Última versión aprobada de cada documento</p>
          </div>
        </div>
        {canWrite && (
          <Button onClick={() => setDialogOpen(true)} data-testid="nuevo-documento-btn">
            <Plus className="w-4 h-4 mr-2" /> Nuevo documento
          </Button>
        )}
      </div>

      {/* Tabla */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : docs.length === 0 ? (
        <div className="text-center py-16 bg-white border border-slate-200 rounded-lg">
          <FileText className="w-12 h-12 mx-auto text-slate-300" />
          <p className="mt-3 text-slate-600 font-medium">No hay documentos cargados</p>
          {canWrite && (
            <p className="text-sm text-slate-400 mt-1">Tocá "Nuevo documento" para empezar.</p>
          )}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto" data-testid="tabla-documentos">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-left text-xs text-slate-500 uppercase">
              <tr>
                <th className="py-2 px-3 font-medium">Título</th>
                <th className="py-2 px-3 font-medium text-center">Versión</th>
                <th className="py-2 px-3 font-medium">Última actualización</th>
                <th className="py-2 px-3 font-medium">Subido por</th>
                <th className="py-2 px-3 font-medium text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => (
                <tr key={d.documento_id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`doc-row-${d.documento_id}`}>
                  <td className="py-2 px-3">
                    <p className="font-medium text-slate-900">{d.titulo}</p>
                    <p className="text-xs text-slate-500 truncate max-w-xs">{d.archivo_nombre}</p>
                  </td>
                  <td className="py-2 px-3 text-center">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-full text-xs font-semibold">
                      v{d.version}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-slate-600 whitespace-nowrap">{formatDateTime(d.fecha_ultima_actualizacion)}</td>
                  <td className="py-2 px-3 text-slate-700">{d.uploaded_by_nombre || '—'}</td>
                  <td className="py-2 px-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <a
                        href={documentosApi.downloadUrl(d.documento_id)}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-green-50 text-green-700 rounded hover:bg-green-100"
                        title="Descargar última versión"
                        data-testid={`download-${d.documento_id}`}
                      >
                        <Download className="w-3.5 h-3.5" />
                        Descargar
                      </a>
                      {(d.versiones_history?.length || 0) > 1 && (
                        <button
                          onClick={() => setHistorialDoc(d)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-slate-50 text-slate-700 rounded hover:bg-slate-100"
                          title="Ver historial de versiones"
                          data-testid={`historial-${d.documento_id}`}
                        >
                          <History className="w-3.5 h-3.5" />
                          Historial
                        </button>
                      )}
                      {canWrite && (
                        <label
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-amber-50 text-amber-700 rounded hover:bg-amber-100 cursor-pointer"
                          title="Subir nueva versión"
                          data-testid={`nueva-version-${d.documento_id}`}
                        >
                          <Upload className="w-3.5 h-3.5" />
                          Nueva versión
                          <input
                            type="file"
                            className="hidden"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) handleNuevaVersion(d, f);
                              e.target.value = '';
                            }}
                          />
                        </label>
                      )}
                      {canDelete && (
                        <button
                          onClick={() => handleDelete(d)}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                          title="Eliminar documento"
                          data-testid={`delete-${d.documento_id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Dialog: Nuevo documento */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setNuevoTitulo(''); setNuevoFile(null); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Nuevo documento</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCrear} className="space-y-4">
            <div>
              <Label htmlFor="doc-titulo">Título</Label>
              <Input
                id="doc-titulo"
                value={nuevoTitulo}
                onChange={(e) => setNuevoTitulo(e.target.value)}
                placeholder="Ej: Contrato de Administración de Propiedades"
                data-testid="nuevo-doc-titulo"
              />
            </div>
            <div>
              <Label htmlFor="doc-file">Archivo</Label>
              <Input
                id="doc-file"
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.txt"
                onChange={(e) => setNuevoFile(e.target.files?.[0] || null)}
                data-testid="nuevo-doc-file"
              />
              {nuevoFile && <p className="text-xs text-slate-500 mt-1">{nuevoFile.name}</p>}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Cancelar</Button>
              <Button type="submit" disabled={saving} data-testid="guardar-doc-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                {saving ? 'Subiendo...' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: Historial de versiones */}
      <Dialog open={!!historialDoc} onOpenChange={(open) => { if (!open) setHistorialDoc(null); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Historial · {historialDoc?.titulo}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {(historialDoc?.versiones_history || []).slice().reverse().map((v) => (
              <div key={v.version} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg border border-slate-200">
                <div>
                  <p className="font-semibold text-slate-900">v{v.version}</p>
                  <p className="text-xs text-slate-500">{v.archivo_nombre}</p>
                  <p className="text-xs text-slate-500">{formatDateTime(v.uploaded_at)} · {v.uploaded_by_nombre}</p>
                </div>
                <a
                  href={documentosApi.downloadUrl(historialDoc.documento_id, v.version)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-green-50 text-green-700 rounded hover:bg-green-100"
                >
                  <Download className="w-3.5 h-3.5" /> Descargar
                </a>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
